import { RTE_NO_VARIABLES } from "@/constants/uiCopy/richTextEditor";
import type { PickerListItem } from "@/features/sparxsEditor/components/molecules/pickerList/pickerList";
import type { DirectiveChipPopoverContentProps } from "@/features/sparxsEditor/components/organisms/richTextEditor/types/directiveChipTypes";
import { PICKER_LIST_POPOVER_CLASS } from "@/features/sparxsEditor/components/organisms/richTextEditor/variables/variableTokenConstants";
import type { DirectiveChoiceSlot } from "../../core/config";
import type { DirectiveBuilderStepPanelsProps } from "./stepPanels";
import { filterDirectiveItems } from "../../suggestion/logic/stepProps";
import { filterVariablesPickerItemsByQuery } from "../../suggestion/logic/variables";

const noopChoiceSubmit = (_value: string): void => {
  void 0;
};

type ChipListBaseFields = Pick<
  DirectiveBuilderStepPanelsProps,
  | "surface"
  | "listFilterText"
  | "onListFilterChange"
  | "onListHighlightChange"
  | "listContainerRef"
  | "listFilterInputRef"
  | "listClassName"
  | "emptyMessage"
>;

const buildChipListBase = (
  input: DirectiveChipPopoverContentProps,
): ChipListBaseFields => ({
  surface: "chip",
  listFilterText: input.chipSegmentFilterText,
  onListFilterChange: input.onChipSegmentFilterChange,
  onListHighlightChange: input.setChipPickerIndex,
  listContainerRef: input.chipPickerKeyboardRef,
  listFilterInputRef: input.chipSegmentFilterInputRef,
  listClassName: PICKER_LIST_POPOVER_CLASS,
  emptyMessage: RTE_NO_VARIABLES,
});

type ChipListStepConfig = {
  step: "directive" | "variable";
  listOptionIdPrefix: string;
  stepTitle: string;
  items: PickerListItem[];
  onSelectItem: (item: PickerListItem) => void;
  firstItemRef?: DirectiveBuilderStepPanelsProps["firstItemRef"];
};

const buildChipListStepPanelsProps = (
  base: ChipListBaseFields,
  input: DirectiveChipPopoverContentProps,
  cfg: ChipListStepConfig,
): DirectiveBuilderStepPanelsProps => ({
  ...base,
  step: cfg.step,
  selectedIndex: input.chipPickerIndex,
  choiceStepKey: 0,
  ...(cfg.firstItemRef != null ? { firstItemRef: cfg.firstItemRef } : {}),
  onListEscape: () => {
    input.setOpen(false);
  },
  listOptionIdPrefix: cfg.listOptionIdPrefix,
  stepProps: {
    items: cfg.items,
    stepTitle: cfg.stepTitle,
    choiceSlot: undefined,
    choiceInitialValue: "",
    onSelectItem: cfg.onSelectItem,
    onChoiceSubmit: noopChoiceSubmit,
  },
});

const chipPanelEditChoiceAtIndex = (
  input: DirectiveChipPopoverContentProps,
  editSegment: number,
): DirectiveBuilderStepPanelsProps | null => {
  const { parsed, config, strategy, allVariables, choicesPart } = input;
  if (config == null) return null;
  const slot =
    strategy != null
      ? strategy.getChoiceSlotForEdit(
          parsed,
          config,
          allVariables,
          editSegment,
          [...choicesPart],
        )
      : config.choiceSlots[editSegment];
  return {
    surface: "chip",
    step: "choice",
    selectedIndex: 0,
    choiceStepKey: editSegment,
    choiceControlVariant: "chip-edit",
    choiceFieldId: `chip-choice-edit-${editSegment}`,
    stepProps: {
      items: [],
      stepTitle: undefined,
      choiceSlot: slot,
      choiceInitialValue: choicesPart[editSegment] ?? "",
      onSelectItem: () => {
        void 0;
      },
      onChoiceSubmit: (value) => {
        input.onUpdateChoiceOnly(editSegment, value);
      },
    },
  };
};

const chipPanelNextChoice = (
  input: DirectiveChipPopoverContentProps,
  slot: DirectiveChoiceSlot,
): DirectiveBuilderStepPanelsProps => {
  const nextIdx = input.parsed.choices.length;
  return {
    surface: "chip",
    step: "choice",
    selectedIndex: 0,
    choiceStepKey: nextIdx,
    choiceControlVariant: "chip-next",
    choiceFieldId: "chip-choice-next",
    chipNextChoiceContext: { strategy: input.strategy },
    choiceInputRef: input.choiceInputRef,
    choiceSelectTriggerRef: input.choiceSelectTriggerRef,
    stepProps: {
      items: [],
      stepTitle: undefined,
      choiceSlot: slot,
      choiceInitialValue: input.choicesPart[nextIdx] ?? "",
      onSelectItem: () => {
        void 0;
      },
      onChoiceSubmit: input.onChoiceSubmit,
    },
  };
};

export const getChipDirectiveBuilderStepPanelProps = (
  input: DirectiveChipPopoverContentProps,
): DirectiveBuilderStepPanelsProps | null => {
  const {
    editSegment,
    needsVariable,
    needsNextChoice,
    nextChoiceSlot,
    filteredVariables,
  } = input;

  const variablePickerItems = filterVariablesPickerItemsByQuery(
    filteredVariables,
    input.chipSegmentFilterText,
  );

  const listBase = buildChipListBase(input);

  if (editSegment === "directive") {
    return buildChipListStepPanelsProps(listBase, input, {
      step: "directive",
      listOptionIdPrefix: "chip-picker-directive",
      stepTitle: "Change directive (resets variable and choices)",
      items: filterDirectiveItems(
        input.allVariables,
        input.chipSegmentFilterText,
      ),
      onSelectItem: (item) =>
        input.onSelectDirective(item.raw.replace(/^@|\/$/g, "")),
    });
  }

  if (editSegment === "variable") {
    return buildChipListStepPanelsProps(listBase, input, {
      step: "variable",
      listOptionIdPrefix: "chip-picker-variable",
      stepTitle: `Change variable for @${input.directiveLabel}`,
      items: variablePickerItems,
      onSelectItem: (item) => {
        input.onUpdateVariableOnly(item.raw);
      },
    });
  }

  if (typeof editSegment === "number") {
    return chipPanelEditChoiceAtIndex(input, editSegment);
  }

  if (editSegment === null && needsVariable) {
    return buildChipListStepPanelsProps(listBase, input, {
      step: "variable",
      listOptionIdPrefix: "chip-picker-needs-var",
      stepTitle: `Select variable for @${input.directiveLabel}`,
      items: variablePickerItems,
      onSelectItem: (item) => {
        input.onSelectVariable(item.raw);
      },
      firstItemRef: input.firstVariableButtonRef,
    });
  }

  if (editSegment === null && needsNextChoice && nextChoiceSlot != null) {
    return chipPanelNextChoice(input, nextChoiceSlot);
  }

  return null;
};
